import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Leaderboard.css';
import TableCell from './TableCell';
import { Radar } from 'react-chartjs-2';
import 'chart.js/auto';
import PromptSample from '../components/PromptSample';

function Leaderboard({ model_class, maxValue=1.0, minValue=0, getRadarPlotData}) {
  const [inputText, setInputText] = useState('gs://griffith-llm-storage/evaldata/leaderboard');
  const [bucket, setBucket] = useState('')
  const location = useLocation();
  const [tableData, setTableData] = useState([]);
  const [sortField, setSortField] = useState(null);
  const [sortSubField, setSortSubField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [activePrompt, setActivePrompt] = useState('');
  const [activeEval, setActiveEval] = useState('');
  const [displayedPrompts, setDisplayedPrompts] = useState([]);
  const [startFrom, setStartFrom] = useState(0);
  const [isExpanded, setIsExpanded] = useState({});
  let backendUrl = '';
  if (process.env.NODE_ENV === 'development') {
    backendUrl = 'http://localhost:8001/';
  }

  const apiEvalsEndpoint = backendUrl + "fetch_llm_evals";
  const apiSamplesEndpoint = backendUrl + "fetch_llm_samples";
  useEffect(() => {
    // Parse query parameters from URL
    const searchParams = new URLSearchParams(location.search);
    const searchParamsBucket = searchParams.get('bucket')
    if (searchParamsBucket) {
      setBucket(searchParamsBucket);
      setInputText(searchParamsBucket);
    } else {
      setBucket('gs://griffith-llm-storage/evaldata/leaderboard');
    }
  }, [location.search]);

  const handleInputTextChange = (e) => {
    setInputText(e.target.value)
  }

  const handleSubmit = () => {
    // Update the "bucket" value when the submit button is clicked
    setBucket(inputText);
  };

  useEffect(() => {
    async function fetchData() {
      console.log("fetching data from: ", bucket)
      try {
        const request = {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            model_class: model_class,
            bucket: bucket
          })
        }
        const response = await fetch(apiEvalsEndpoint, request);
        const jsonData = await response.json();
        setTableData(jsonData);  // nested dictionary of the form
        // <fileName> : <promptName> : <metricName> : <score>
        setIsExpanded({})
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
    fetchData();
  }, [apiEvalsEndpoint, bucket, model_class]);

  useEffect(() => {
    async function fetchSamples() {
      try {
        const request = {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            eval_name: activeEval,
            prompt_name: activePrompt,
            start_from: startFrom,
            bucket: bucket
          })
        }
        const response = await fetch(apiSamplesEndpoint, request);
        const jsonData = await response.json();
        setDisplayedPrompts(jsonData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    }
    if (activePrompt.length > 0 && activeEval.length > 0) {
      fetchSamples();
    }
  }, [apiSamplesEndpoint, bucket, activePrompt, activeEval, startFrom]);

  const sortedData = Object.entries(tableData).map(([key,value])=> ({fileName:key, ...value}))  // array of the form
  // [fileName : <fileName>, <promptName> : <metricName> : <score>]

  if (sortField) {
    sortedData.sort((a, b) => {
      const aValue = sortSubField && a[sortField] ? a[sortField][sortSubField] : a[sortField];
      const bValue = sortSubField && b[sortField] ? b[sortField][sortSubField] : b[sortField];

      if ((typeof aValue == "undefined") || aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if ((typeof bValue == "undefined") || aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  const handleSort = (field, subfield) => {
    setSortField(field);
    setSortSubField(subfield);
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const handleCheckboxChange = (key) => {
    setIsExpanded({ ...isExpanded, [key]: !isExpanded[key] });
  };
  const handleUncheckAll = () => {
    setIsExpanded(Object.fromEntries(columnGroups.map(x => [x, false])));
  };

  // Extract column groups and subcolumns
  var subColumns = {};
  var columnGroups = [];

  if (Object.keys(tableData).length > 0) {
    subColumns = getSubColumns(tableData);
    columnGroups = Object.keys(subColumns);
    if (Object.keys(isExpanded).length===0) {
      setIsExpanded(Object.fromEntries(columnGroups.map(x => [x, true])))
    }
  }

  subColumns = Object.fromEntries(
    Object.entries(subColumns).filter(([key]) => isExpanded[key])
    );


  return (
    <div>
      Eval data bucket path:
      <input
        type="text"
        placeholder="Edit Bucket Value..."
        value={inputText}
        onChange={handleInputTextChange}
        style={{
          width: '500px'
        }} />
      <button onClick={handleSubmit}>Submit</button>
      <div>
        {Object.keys(isExpanded).map((key) => (
          <div key={key}>
            <label>
              <input
                type="checkbox"
                checked={isExpanded[key]}
                onChange={() => handleCheckboxChange(key)}
              />
              {key}
            </label>
          </div>
        ))}
        <div key="uncheck_all">
            <label>
              <input
                type="checkbox"
                checked={Object.values(isExpanded).every(e=>e===false)}
                onChange={() => handleUncheckAll()}
              />
              {"uncheck_all"}
            </label>
          </div>
      </div>
      <table className="myTable">
        <thead>
        <tr>
            <th>{model_class}</th>
            {Object.entries(subColumns).map(([promptName, col]) => (
              <th
                colSpan={col.length}
                key={promptName}
                onClick={() => setActivePrompt(promptName)}
                style={promptName === activePrompt ? { backgroundColor: 'green' } : {}}
              >
                {promptName}
              </th>
            ))}
          </tr>
          <tr>
            <th onClick={() => handleSort('fileName', null)}>
              File Name {sortField === 'fileName' && (sortOrder === 'asc' ? '↑' : '↓')}
            </th>
            {Object.entries(subColumns).map(([promptName, col]) => (
              col.map(metricName => (
                <th
                  key={`${promptName}-${metricName}`}
                  onClick={() => handleSort(promptName, metricName)}
                >
                  {metricName} {sortField === promptName && sortSubField === metricName && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
              ))
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, index) => (
            <tr key={index}>
              <td style={
                row.fileName === activeEval ?
                  { color: stringToColor(row.fileName), backgroundColor: 'green' } :
                  { color: stringToColor(row.fileName) }
              }
                onClick={() => setActiveEval(row.fileName)}>
                {row.fileName}
              </td>
              {Object.entries(subColumns).map(([promptName, col]) =>  row[promptName] ? (
                col.map((metricName) => (
                  <TableCell key={`${row.fileName}-${promptName}-${metricName}`} maxValue={maxValue} minValue={minValue}
                    value={formatScore(row[promptName][metricName])}
                  />
                ))
              ) : (
                Object.entries(col).map(i =>
                  <TableCell key={`${row.fileName}-${promptName}-${i}`}/>
                )
              )
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {displayedPrompts.map((prompt) =>
        <PromptSample
          prompt={prompt}
          key={prompt.fileName}
        />)}
      <>
      start from: {startFrom}
        <button onClick={() => setStartFrom(startFrom - 3)}>
          prev
        </button>
        <button onClick={() => setStartFrom(startFrom + 3)}>
          next
        </button>
      </>

      <div style={{
        width: '600px'
      }}>
        <Radar data={getRadarPlotData(tableData)} options={{
          animation: {
            duration: 0
          }
        }} />
      </div>
    </div>
  );
};

function formatScore(score) {
  return typeof score === 'number' ? score.toFixed(4).replace(/(?:\.0+|(\.\d+?)0+)$/, '$1') : score;
}


function stringToColor(str) {
  // Use a simple hash function to generate a numeric hash value from the string
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert the hash value to a positive number
  hash = Math.abs(hash);

  // Create a hexadecimal color code
  const color = '#' + (hash % 0xFFFFFF).toString(16).padStart(6, '0');

  return color;
}


function getSubColumns(tableData) {
  let subColumns = {};
  Object.values(tableData).forEach((row) => {
    Object.entries(row).forEach(([key, value]) => {
      // make sure subColumns include all keys in any rows
      // key: prompt type, eg. SkipFormFieldPrompt
      if (!Object.keys(subColumns).includes(key)) {
        subColumns[key] = [];
      }
      // subkey: metric type, eg. accuracy
      Object.keys(value).forEach(subKey => {
        if (!subColumns[key].includes(subKey)) {
          subColumns[key].push(subKey);
        }
      });
    });
  });
  return subColumns
}


function culverRadarPlotData(tableData) {
  let labels = [];
  let datasets = [];
  Object.entries(tableData).forEach(([fileName, row]) => {
    const {total, ...radarMetrics} = row['FewShotDialogPrompt'];
    const data = Object.values(radarMetrics);
    labels = Object.keys(radarMetrics);
    const color = stringToColor(fileName);
    datasets.push({
      label: fileName,
      backgroundColor: color + '30',
      borderColor: color,
      pointBackgroundColor: color,
      data: data
    })
  });
  return {
    labels: labels,
    datasets: datasets
  }
};

function silverlakeRadarPlotData(tableData) {
  let labels = [];
  let datasets = [];
  let subColumns = getSubColumns(tableData);
  let columnGroups = Object.keys(subColumns);
  Object.entries(tableData).forEach(([fileName, row]) => {
    const data = columnGroups.map((promptName) => (promptName in row? row[promptName].accuracy:null))
    labels = columnGroups;
    const color = stringToColor(fileName);
    datasets.push({
      label: fileName,
      backgroundColor: color + '30',
      borderColor: color,
      pointBackgroundColor: color,
      data: data
    })
  });
  return {
    labels: labels,
    datasets: datasets
  }
};

function executiveMetricsRadarPlotData(tableData) {
  let labels = [];
  let datasets = [];
  let subColumns = getSubColumns(tableData);
  let columnGroups = Object.keys(subColumns);
  Object.entries(tableData).forEach(([fileName, row]) => {
    const data = columnGroups.map((testName) => (testName in row? row[testName].correct/row[testName].perfect:null))
    labels = columnGroups;
    const color = stringToColor(fileName);
    datasets.push({
      label: fileName,
      backgroundColor: color + '30',
      borderColor: color,
      pointBackgroundColor: color,
      data: data
    })
  });
  return {
    labels: labels,
    datasets: datasets
  }
};

export default function App() {
  return (
    <>
      <Leaderboard model_class={'silverlake'} getRadarPlotData={silverlakeRadarPlotData}/>
      <Leaderboard model_class={'culver'} minValue={-1.0} getRadarPlotData={culverRadarPlotData}/>
      <Leaderboard model_class={'executive_metrics'} getRadarPlotData={executiveMetricsRadarPlotData}/>
    </>
  )
};